/**
 * Header template search (desktop icon + mobile bar). Alpine component
 * factory registered on window by app.js before Alpine.start().
 *
 * The local index (180 most-downloaded templates + document types) is fetched
 * from /api/templates/search-payload the first time the box opens; until
 * Sep 16 2026 it was inlined into every page as an 86 KB JSON block.
 */
export default function ltMobileTemplateSearch(payloadUrl) {
    const normalize = function(value) {
        return String(value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    };
    const withSearchData = function(item) {
        const normalizedTitle = normalize(item.title);
        const normalizedSearch = normalize([item.title, item.typeLabel, item.search, item.summary].join(' '));

        return Object.assign({}, item, {
            normalizedTitle: normalizedTitle,
            normalizedSearch: normalizedSearch,
        });
    };
    const scoreItem = function(item, query, tokens) {
        let score = 0;

        if (!query) {
            return score;
        }

        if (item.normalizedTitle === query) {
            score += 120;
        } else if (item.normalizedTitle.startsWith(query)) {
            score += 70;
        } else if (item.normalizedTitle.includes(query)) {
            score += 45;
        }

        if (item.normalizedSearch.includes(query)) {
            score += 24;
        }

        tokens.forEach(function(token) {
            if (item.normalizedTitle.split(' ').includes(token)) {
                score += 18;
            } else if (item.normalizedTitle.includes(token)) {
                score += 12;
            } else if (item.normalizedSearch.includes(token)) {
                score += 5;
            }
        });

        if (item.guided) {
            score += 3;
        }

        score += Math.min(Number(item.downloads || 0) / 1000, 8);

        return score;
    };
    const itemMatchesQuery = function(item, query, tokens) {
        if (!query) {
            return true;
        }

        if (item.normalizedSearch.includes(query)) {
            return true;
        }

        return tokens.length > 0 && tokens.every(function(token) {
            return item.normalizedSearch.includes(token);
        });
    };
    // The index is fetched on first open (see ensureLoaded) instead of being
    // inlined into every page.
    const prepareItems = function(rawItems) {
        return Array.isArray(rawItems) ? rawItems.map(withSearchData) : [];
    };
    const prepareTypes = function(rawTypes) {
        return Array.isArray(rawTypes)
            ? rawTypes.map(function(type) {
                return Object.assign({}, type, {
                    normalizedLabel: normalize([type.label, type.documentType].join(' ')),
                });
            })
            : [];
    };
    let payloadPromise = null;

    return {
        query: '',
        open: false,
        items: [],
        types: [],
        localItemsByPath: {},
        loaded: false,
        allTemplatesUrl: '/all-templates',
        generatorUrl: '/ai-document/generator',
        remoteItems: null,
        remoteFor: '',
        remoteFetchTimer: null,
        remoteFetchSeq: 0,
        remoteNavigation: null,
        remoteLogId: null,

        init() {
            this.$watch('query', () => this.scheduleRemoteSearch());
            this.$watch('open', (open) => {
                if (open) {
                    this.ensureLoaded();
                }
            });
        },

        // Fetch the local index once per page; a second component on the same
        // page (desktop + mobile search) shares the in-flight request.
        ensureLoaded() {
            if (this.loaded || !payloadUrl) {
                return Promise.resolve();
            }

            if (!payloadPromise) {
                payloadPromise = fetch(payloadUrl, { headers: { 'Accept': 'application/json' } })
                    .then(function(response) {
                        if (!response.ok) {
                            throw new Error('Template index unavailable');
                        }
                        return response.json();
                    })
                    .catch(function() {
                        return {};
                    });
            }

            return payloadPromise.then((payload) => {
                this.allTemplatesUrl = payload.allTemplatesUrl || this.allTemplatesUrl;
                this.generatorUrl = payload.generatorUrl || this.generatorUrl;
                this.items = prepareItems(payload.items);
                this.types = prepareTypes(payload.types);
                const byPath = {};
                this.items.forEach(function(item) {
                    try {
                        byPath[new URL(item.url, window.location.origin).pathname] = item;
                    } catch (error) {
                        // Ignore unparseable URLs; the item stays reachable via local scoring.
                    }
                });
                this.localItemsByPath = byPath;
                this.loaded = true;
            });
        },

        get trimmedQuery() {
            return this.query.trim();
        },

        get normalizedQuery() {
            return normalize(this.query);
        },

        // The embedded payload only carries the 180 most-downloaded templates,
        // so long-tail matches come from the server (same engine as
        // /all-templates). Local scoring answers instantly; the server result
        // replaces it once fetched and is authoritative for "no matches".
        get remoteFresh() {
            return this.remoteItems !== null && this.remoteFor === this.trimmedQuery;
        },

        scheduleRemoteSearch() {
            window.clearTimeout(this.remoteFetchTimer);
            const query = this.trimmedQuery;

            if (query.length < 2) {
                this.remoteItems = null;
                this.remoteFor = '';
                this.remoteNavigation = null;
                this.remoteLogId = null;
                return;
            }

            this.remoteFetchTimer = window.setTimeout(() => this.fetchRemoteSearch(query), 220);
        },

        fetchRemoteSearch(query) {
            const seq = ++this.remoteFetchSeq;

            fetch('/api/forms/search?q=' + encodeURIComponent(query), {
                headers: { 'Accept': 'application/json' }
            })
                .then(function(response) {
                    if (!response.ok) {
                        throw new Error('Search unavailable');
                    }
                    return response.json();
                })
                .then((data) => {
                    if (seq !== this.remoteFetchSeq) {
                        return;
                    }

                    const results = Array.isArray(data.results) ? data.results : [];
                    this.remoteItems = results.map(function(result) {
                        const local = this.localItemsByPath[String(result.url || '').split('?')[0]];
                        if (local) {
                            return Object.assign({}, local, { categoryId: result.id || null });
                        }

                        return withSearchData({
                            title: result.name,
                            typeLabel: result.type || 'Template',
                            url: result.url,
                            search: [result.name, result.type].join(' '),
                            summary: '',
                            downloads: 0,
                            guided: Boolean(result.guided),
                            categoryId: result.id || null,
                        });
                    });
                    this.remoteNavigation = data.navigation && data.navigation.url ? data.navigation : null;
                    this.remoteLogId = data.log_id || null;
                    this.remoteFor = query;
                })
                .catch(() => {
                    // Server search unavailable: keep showing local matches.
                });
        },

        get matchingItems() {
            const query = this.normalizedQuery;
            const tokens = query.split(/\s+/).filter(Boolean);

            if (!query) {
                return this.items.slice(0, 6);
            }

            if (this.remoteFresh) {
                return this.remoteItems.slice(0, 6);
            }

            return this.items
                .map(function(item) {
                    return Object.assign({}, item, {
                        score: scoreItem(item, query, tokens),
                    });
                })
                .filter(function(item) {
                    return item.score > 0
                        && itemMatchesQuery(item, query, tokens);
                })
                .sort(function(a, b) {
                    if (b.score !== a.score) {
                        return b.score - a.score;
                    }

                    if ((b.downloads || 0) !== (a.downloads || 0)) {
                        return (b.downloads || 0) - (a.downloads || 0);
                    }

                    return a.title.localeCompare(b.title);
                })
                .slice(0, 6);
        },

        get matchingTypes() {
            const query = this.normalizedQuery;

            if (!query) {
                return this.types.slice(0, 4);
            }

            return this.types
                .filter(function(type) {
                    return type.normalizedLabel.includes(query)
                        || query.split(/\s+/).some(function(token) {
                            return token && type.normalizedLabel.includes(token);
                        });
                })
                .slice(0, 4);
        },

        get hasSuggestions() {
            return this.matchingItems.length > 0 || this.matchingTypes.length > 0 || this.trimmedQuery !== '';
        },

        get resultsAnnouncement() {
            const count = this.matchingItems.length;

            if (count > 0) {
                return count === 1 ? '1 template found' : count + ' templates found';
            }

            if (this.trimmedQuery !== '') {
                return 'No matches — you can generate a custom document';
            }

            return '';
        },

        showSuggestions() {
            this.ensureLoaded();
            this.open = true;
        },

        handlePanelFocusOut(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.open = false;
            }
        },

        clearSearch() {
            this.query = '';
            this.open = true;
            this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
        },

        searchUrl() {
            if (!this.trimmedQuery) {
                return this.allTemplatesUrl;
            }

            const separator = this.allTemplatesUrl.includes('?') ? '&' : '?';
            return this.allTemplatesUrl + separator + 'search=' + encodeURIComponent(this.trimmedQuery);
        },

        generatorSearchUrl() {
            if (!this.trimmedQuery) {
                return this.generatorUrl;
            }

            const separator = this.generatorUrl.includes('?') ? '&' : '?';
            return this.generatorUrl + separator + 'search=' + encodeURIComponent(this.trimmedQuery);
        },

        // The account link ("dashboard", "cancel subscription") for the
        // current query, once the server has answered.
        get navigationItem() {
            return this.remoteFresh ? this.remoteNavigation : null;
        },

        // Search log: which suggestion was chosen. Fire-and-forget.
        report(outcome, categoryId) {
            if (!this.remoteFresh || !this.remoteLogId) {
                return;
            }
            try {
                const body = JSON.stringify({ id: this.remoteLogId, category_id: categoryId || null, outcome: outcome });
                if (navigator.sendBeacon) {
                    navigator.sendBeacon('/api/search/click', new Blob([body], { type: 'application/json' }));
                }
            } catch (error) {
                // Never block navigation for logging.
            }
        },

        go(url, item) {
            if (url) {
                if (item && item.categoryId) {
                    this.report('click', item.categoryId);
                }
                window.location.href = url;
            }
        },

        goNavigation() {
            if (this.navigationItem) {
                this.report('navigation');
                window.location.href = this.navigationItem.url;
            }
        },

        submit() {
            const query = this.normalizedQuery;

            if (!query) {
                window.location.href = this.allTemplatesUrl;
                return;
            }

            const matches = this.matchingItems;
            const exactMatch = matches.find(function(item) {
                return item.normalizedTitle === query;
            }) || this.items.find(function(item) {
                return item.normalizedTitle === query;
            });

            if (exactMatch) {
                this.go(exactMatch.url);
                return;
            }

            if (!this.remoteFresh && matches.length === 1 && matches[0].score >= 85) {
                this.go(matches[0].url);
                return;
            }

            if (matches.length === 0) {
                this.report('generator');
            }

            window.location.href = matches.length > 0
                ? this.searchUrl()
                : this.generatorSearchUrl();
        },
    };
}

window.ltMobileTemplateSearch = window.ltMobileTemplateSearch || ltMobileTemplateSearch;
